import React, { useState, useEffect } from 'react';
import './ChoiceBox.css';

export interface Choice {
  id: string;
  text: string;
  // オプション: 選択肢の見た目をカスタマイズ
  className?: string;
  disabled?: boolean;
}

interface ChoiceBoxProps {
  visible: boolean;
  question?: string;
  choices: Choice[];
  onSelect: (choiceId: string) => void;
  onTimeout?: () => void;
  timeoutSeconds?: number; // 自動で消える時間（秒）
  position?: 'center' | 'bottom' | 'top';
}

const ChoiceBox: React.FC<ChoiceBoxProps> = ({
  visible,
  question,
  choices,
  onSelect,
  onTimeout,
  timeoutSeconds,
  position = 'center'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setIsAnimating(true);
      setSelectedId(null);
      return;
    }
    // 非表示になったら退場アニメーション（0.3s）を待ってアンマウントする
    // （タイムアウトで閉じた場合もDOMに残さない）
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, [visible]);

  // タイムアウト: 選択済みになったらタイマーを破棄して発火させない
  useEffect(() => {
    if (visible && timeoutSeconds && onTimeout && !selectedId) {
      const timer = setTimeout(() => {
        onTimeout();
      }, timeoutSeconds * 1000);
      return () => clearTimeout(timer);
    }
  }, [visible, timeoutSeconds, onTimeout, selectedId]);

  const handleSelect = (choiceId: string) => {
    if (selectedId) return; // 既に選択済みなら無視
    if (!visible) return; // タイムアウト後の退場アニメーション中は無視

    setSelectedId(choiceId);

    // アニメーション後にコールバック
    setTimeout(() => {
      onSelect(choiceId);
    }, 300);
  };

  if (!visible && !isAnimating) return null;

  return (
    <div id="galge-choice-overlay" className={`choice-box-overlay ${visible ? 'visible' : ''} ${position}`}>
      <div id="galge-choice-container" className={`choice-box-container ${visible ? 'visible' : ''}`}>
        {question && (
          <div className="choice-box-question">
            <p className="choice-box-question-text">{question}</p>
          </div>
        )}
        <div id="galge-choice-list" className="choice-box-choices">
          {choices.map((choice, index) => (
            <button
              id={`galge-choice-${index}`}
              key={choice.id}
              className={`choice-box-choice ${choice.className || ''} ${
                selectedId === choice.id ? 'selected' : ''
              } ${selectedId && selectedId !== choice.id ? 'not-selected' : ''}`}
              onClick={() => handleSelect(choice.id)}
              disabled={choice.disabled || !!selectedId}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              <span className="choice-box-choice-arrow">▶</span>
              <span className="choice-box-choice-text">{choice.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChoiceBox;